import { Head, router, useForm, usePage } from '@inertiajs/react'
import { Badge, Button, Group, NativeSelect, Paper, SimpleGrid, Stack, Text, TextInput, Textarea, Title, UnstyledButton } from '@mantine/core'
import { type FormEvent, useState } from 'react'
import AssignSkillForm from '@/components/AssignSkillForm'
import EmptyState from '@/components/EmptyState'
import FileBrowser from '@/components/FileBrowser'
import PageLayout from '@/components/PageLayout'
import SkillAssignmentRow from '@/components/SkillAssignmentRow'
import { ProjectTypeBadge } from '@/components/badges'
import { submitHiddenForm } from '@/lib/formSubmission'
import type { AgentProps, ProjectProps, ProjectShowPageProps, SharedProps, WikiPageSummaryProps } from '@/types'

function IssuesSection({ projectId, openIssueCount }: { projectId: string; openIssueCount: number }) {
  return (
    <Paper p="lg">
      <Group justify="space-between" align="center">
        <div>
          <Title order={4}>Issues</Title>
          <Text c="dimmed" size="sm">Track work, bugs, and open questions.</Text>
        </div>
        <Group>
          {openIssueCount > 0 ? <Badge color="teal" variant="light">{openIssueCount} open</Badge> : null}
          <Button variant="default" onClick={() => router.visit(`/projects/${projectId}/issues`)}>
            View all issues
          </Button>
        </Group>
      </Group>
    </Paper>
  )
}

function WikiPageRow({ projectId, wikiPage }: { projectId: string; wikiPage: WikiPageSummaryProps }) {
  return (
    <UnstyledButton onClick={() => router.visit(`/projects/${projectId}/wiki_pages/${wikiPage.id}`)}>
      <Paper p="md" withBorder>
        <Stack gap={4}>
          <Text fw={500}>{wikiPage.title}</Text>
          <Text c="dimmed" size="sm">{wikiPage.path}</Text>
        </Stack>
      </Paper>
    </UnstyledButton>
  )
}

function NewWikiPageForm({ projectId }: { projectId: string }) {
  const { csrf_token, errors: pageErrors } = usePage<{ csrf_token: string; errors: Record<string, string[]> }>().props
  const hasPageErrors = pageErrors && Object.keys(pageErrors).some((key) => key.startsWith('wiki_page.'))
  const [open, setOpen] = useState(hasPageErrors)

  const { data, setData, post, processing, errors: formErrors, reset } = useForm({
    wiki_page: { title: '', path: '', body: '' },
    authenticity_token: csrf_token,
  })

  const errors = {
    title: formErrors['wiki_page.title'] || pageErrors?.['wiki_page.title'],
    path: formErrors['wiki_page.path'] || pageErrors?.['wiki_page.path'],
  }

  const formatError = (value: string | string[] | undefined) => {
    if (!value) return undefined
    return Array.isArray(value) ? value.join(', ') : value
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/wiki_pages`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
      },
    })
  }

  if (!open) {
    return (
      <Button variant="light" onClick={() => setOpen(true)}>
        + New Page
      </Button>
    )
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg">
      <Stack gap="md">
        <TextInput
          id="wp-title"
          label="Title"
          value={data.wiki_page.title}
          onChange={(e) => setData('wiki_page', { ...data.wiki_page, title: e.target.value })}
          error={formatError(errors.title)}
        />

        <TextInput
          id="wp-path"
          label="Path"
          value={data.wiki_page.path}
          onChange={(e) => setData('wiki_page', { ...data.wiki_page, path: e.target.value })}
          error={formatError(errors.path)}
        />

        <Textarea
          id="wp-body"
          label="Body"
          autosize
          minRows={4}
          value={data.wiki_page.body}
          onChange={(e) => setData('wiki_page', { ...data.wiki_page, body: e.target.value })}
        />

        <Group>
          <Button type="submit" loading={processing}>Create Page</Button>
          <Button type="button" variant="default" onClick={() => { reset(); setOpen(false) }}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function ChildProjectRow({ child }: { child: ProjectProps }) {
  return (
    <UnstyledButton onClick={() => router.visit(`/projects/${child.id}`)}>
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4} style={{ flex: 1 }}>
            <Text fw={500}>{child.name}</Text>
            {child.description ? <Text c="dimmed" size="sm">{child.description}</Text> : null}
          </Stack>
          <ProjectTypeBadge type={child.project_type} />
        </Group>
      </Paper>
    </UnstyledButton>
  )
}

function NewSubProjectForm({ parentId, orgPrefix }: { parentId: string; orgPrefix: string }) {
  const { csrf_token } = usePage<ProjectShowPageProps>().props
  const [open, setOpen] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    project: { name: '', description: '', project_type: 'project', parent_project_id: String(parentId) },
    authenticity_token: csrf_token || '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`${orgPrefix}/projects`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
      },
    })
  }

  if (!open) {
    return (
      <Button variant="light" onClick={() => setOpen(true)}>
        + New Sub-project
      </Button>
    )
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg">
      <Stack gap="md">
        <TextInput
          id="sub-name"
          label="Name"
          value={data.project.name}
          onChange={(e) => setData('project', { ...data.project, name: e.target.value })}
        />

        <Textarea
          id="sub-desc"
          label="Description"
          autosize
          minRows={2}
          value={data.project.description}
          onChange={(e) => setData('project', { ...data.project, description: e.target.value })}
        />

        <NativeSelect
          id="sub-type"
          label="Type"
          value={data.project.project_type}
          onChange={(e) => setData('project', { ...data.project, project_type: e.target.value })}
          data={[
            { value: 'project', label: 'Project' },
            { value: 'folder', label: 'Folder' },
          ]}
        />

        <Group>
          <Button type="submit" loading={processing}>Create</Button>
          <Button type="button" variant="default" onClick={() => { reset(); setOpen(false) }}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function NewSessionSection({ agents, projectId, csrfToken }: { agents: AgentProps[]; projectId: string; csrfToken: string }) {
  const [selectedAgentId, setSelectedAgentId] = useState(agents[0]?.id || '')

  if (agents.length === 0) return null

  return (
    <Paper p="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Start new session</Title>
          <Text c="dimmed" size="sm">Launch an agent directly inside this project.</Text>
        </div>

        <Group align="flex-end">
          {agents.length > 1 ? (
            <NativeSelect
              label="Agent"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              data={agents.map((agent) => ({ value: agent.id, label: `${agent.name} (${agent.role})` }))}
            />
          ) : (
            <Text>{agents[0].name}</Text>
          )}

          <Button
            onClick={() => submitHiddenForm({
              action: `/agents/${selectedAgentId}/sessions`,
              values: {
                authenticity_token: csrfToken,
                project_id: projectId,
              },
            })}
          >
            + New Chat
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

export default function ProjectShow() {
  const { project, wiki_pages, children, ancestors, csrf_token, skill_assignments, available_skills, agents, open_issue_count } = usePage<ProjectShowPageProps>().props
  const { current_organization } = usePage<SharedProps>().props
  const orgPrefix = current_organization ? `/orgs/${current_organization.slug}` : ''

  return (
    <>
      <Head title={project.name} />

      <PageLayout
        title={project.name}
        subtitle={project.description || 'Project workspace'}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          ...ancestors.map((ancestor) => ({ label: ancestor.name, href: `/projects/${ancestor.id}` })),
          { label: project.name },
        ]}
        size="lg"
      >
        <Stack gap="xl">
          <NewSessionSection agents={agents} projectId={project.id} csrfToken={csrf_token as string} />

          <IssuesSection projectId={project.id} openIssueCount={open_issue_count} />

          <div>
            <Group justify="space-between" align="center" mb="md">
              <div>
                <Title order={3}>Sub-projects</Title>
                <Text c="dimmed" size="sm">Nested folders and projects for this workspace.</Text>
              </div>
              <NewSubProjectForm parentId={project.id} orgPrefix={orgPrefix} />
            </Group>

            {children.length > 0 ? (
              <Stack gap="sm">
                {children.map((child) => (
                  <ChildProjectRow key={child.id} child={child} />
                ))}
              </Stack>
            ) : (
              <EmptyState>No sub-projects yet.</EmptyState>
            )}
          </div>

          <div>
            <Title order={3} mb="md">Skills</Title>
            <Stack gap="md">
              <AssignSkillForm mode="project" availableSkills={available_skills} assignableType="Project" assignableId={project.id} />
              {skill_assignments.length > 0 ? (
                <Stack gap="sm">
                  {skill_assignments.map((assignment) => (
                    <SkillAssignmentRow key={assignment.id} assignment={assignment} />
                  ))}
                </Stack>
              ) : (
                <EmptyState>No skills assigned to this project.</EmptyState>
              )}
            </Stack>
          </div>

          <div>
            <Title order={3} mb="md">Files</Title>
            <FileBrowser projectId={project.id} csrfToken={csrf_token} />
          </div>

          <div>
            <Group justify="space-between" align="center" mb="md">
              <div>
                <Title order={3}>Wiki pages</Title>
                <Text c="dimmed" size="sm">Notes, docs, and project-specific instructions.</Text>
              </div>
              <NewWikiPageForm projectId={project.id} />
            </Group>

            {wiki_pages.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                {wiki_pages.map((wikiPage) => (
                  <WikiPageRow key={wikiPage.id} projectId={project.id} wikiPage={wikiPage} />
                ))}
              </SimpleGrid>
            ) : (
              <EmptyState>No wiki pages yet.</EmptyState>
            )}
          </div>
        </Stack>
      </PageLayout>
    </>
  )
}
